import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  FiGrid, FiFileText, FiUsers, FiBarChart2, FiSettings,
  FiMenu, FiX, FiLogOut, FiChevronDown, FiActivity,
  FiClipboard, FiUserCheck, FiBell
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { to: '/admin', icon: FiGrid, label: 'Dashboard', end: true },
  { to: '/admin/exams', icon: FiFileText, label: 'Exams' },
  { to: '/admin/candidates', icon: FiUsers, label: 'Candidates' },
  { to: '/admin/results', icon: FiBarChart2, label: 'Results' },
  { to: '/admin/shortlist', icon: FiUserCheck, label: 'Shortlist' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-100">
        <div className="w-9 h-9 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-white font-black text-base">A</span>
        </div>
        <div>
          <div className="font-black text-gray-900 text-base leading-tight">AssessHub</div>
          <div className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-3 py-2 mt-1">Main Menu</p>
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}>
            <Icon size={18} />
            {label}
          </NavLink>
        ))}

        {user?.role === 'super_admin' && (
          <>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-3 py-2 mt-3">Super Admin</p>
            <NavLink to="/super-admin" end className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <FiActivity size={18} /> System Overview
            </NavLink>
            <NavLink to="/super-admin/admins" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <FiClipboard size={18} /> Manage Admins
            </NavLink>
          </>
        )}
      </nav>

      {/* User profile */}
      <div className="p-3 border-t border-gray-100">
        <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer group"
          onClick={() => setProfileOpen(o => !o)}>
          <div className="w-9 h-9 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-navy font-bold text-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{user?.first_name} {user?.last_name}</div>
            <div className="text-xs text-gray-400 truncate">{user?.email}</div>
          </div>
          <FiChevronDown size={14} className={`text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
        </div>
        {profileOpen && (
          <div className="mt-1 py-1 border border-gray-100 rounded-xl bg-white shadow-lg">
            <button onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-medium">
              <FiLogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-gray-100 flex-shrink-0">
        <Sidebar />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white z-50 shadow-xl">
            <Sidebar />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-6 py-3 flex items-center gap-4 flex-shrink-0">
          <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setSidebarOpen(true)}>
            <FiMenu size={20} />
          </button>
          <div className="flex-1" />
          <button className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <FiBell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-6 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
